import { Suspense, lazy } from "react";
import { Phone, Mail, ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SolutionObject = lazy(() => import("./three/SolutionObject"));

export default function SolutionModal({ solution, onClose, onTalk }) {
  return (
    <Dialog open={!!solution} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="solution-modal"
        data-lenis-prevent
        className="bg-charcoal-800 border border-gold/25 text-ivory rounded-md sm:max-w-lg max-h-[88vh] overflow-y-auto backdrop-blur-2xl"
      >
        {solution && (
          <div className="relative">
            <div className="absolute top-4 right-6 w-24 h-24 pointer-events-none rounded-full bg-black/35 border border-gold/25 overflow-hidden" aria-hidden="true">
              <Suspense fallback={null}>
                <SolutionObject type={solution.obj} />
              </Suspense>
            </div>

            <p className="gold-label mb-3">Mortgage Solution</p>
            <DialogTitle data-testid="solution-modal-title" className="font-serif text-3xl text-ivory pr-24">
              {solution.title}
            </DialogTitle>
            <DialogDescription className="text-ivory/50 font-light mt-2">
              {solution.desc}
            </DialogDescription>

            <div className="mt-8 space-y-7">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-gold mb-2.5">Who It's For</p>
                <p className="text-ivory/65 font-light text-[15px] leading-relaxed">{solution.forWhom}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-gold mb-2.5">Overview</p>
                <p className="text-ivory/65 font-light text-[15px] leading-relaxed">{solution.overview}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3">Key Considerations</p>
                <ul className="space-y-2.5">
                  {solution.considerations.map((c) => (
                    <li key={c} className="flex gap-3 text-ivory/60 font-light text-sm leading-relaxed">
                      <Check size={15} strokeWidth={1.75} className="text-gold shrink-0 mt-0.5" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-9 pt-7 border-t border-white/10 space-y-3.5">
              <button
                data-testid="solution-talk-btn"
                onClick={onTalk}
                className="w-full inline-flex items-center justify-center gap-2.5 px-6 py-4 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
              >
                TALK WITH AKASHDEEP <ArrowRight size={14} strokeWidth={1.75} />
              </button>
              <div className="grid grid-cols-2 gap-3.5">
                <a
                  href="tel:+18257362193"
                  data-testid="solution-call-link"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3.5 text-[11px] tracking-[0.14em] font-medium text-ivory border border-white/20 hover:border-gold/60 hover:text-gold transition-colors duration-300 rounded-sm"
                >
                  <Phone size={13} strokeWidth={1.75} /> (825) 736-2193
                </a>
                <a
                  href="mailto:akkasghai@gmail.com"
                  data-testid="solution-email-link"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3.5 text-[11px] tracking-[0.14em] font-medium text-ivory border border-white/20 hover:border-gold/60 hover:text-gold transition-colors duration-300 rounded-sm"
                >
                  <Mail size={13} strokeWidth={1.75} /> EMAIL
                </a>
              </div>
              <p className="text-[10px] text-ivory/30 leading-relaxed text-center pt-1">
                General information only — not a mortgage commitment, rate hold or guarantee. Every file is reviewed individually.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
